import { error_something_wrong_button } from '@porterin/feature/src/containers/fare-estimate-v1/domain/store/FareEstimateStoreV1';
import { useFareEstimateContext } from '@porterin/feature/src/contexts/fare-estimate-context/FareEstimateContextV1';
import { useHook as useFareEstimateRetryHook } from '@porterin/feature/src/shared/fare-estimate/view/hooks/FareEstimateRetryHook.mjs';
import FareEstimateErrorCmp from '@porterin/feature/src/shared/lead-flow/view/component/FareEstimateErrorCmp';
import {
  error_invalid_location,
  error_something_went_wrong,
  error_something_wrong,
  getDropOffButtonLabel,
  getErrorUnserviceableDropoff,
  getErrorUnserviceablePickup,
  getPickupButtonLabel,
} from '@porterin/feature/src/shared/lead-flow/view/view-model/FareEstimateVm';
import { MODAL_CLOSE_TIMEOUT_NONE } from '@porterin/feature/src/shared/modal/domain/entities/ModalEntities';
import { useRouter } from 'next/router';
import React from 'react';
import ReactModal from 'react-modal';

import { crossIcon } from '../assets/AssetStore';
import styles from '../styles/common/BookingDetails.module.scss';

import NextImageV2 from './NextImageV2';

export const FARE_ESTIMATE_ERROR = {
  INVALID_TOKEN: 0,
  INVALID_PICKUP: 1,
  INVALID_DROP: 2,
  FAILED_API: 3,
  FAILED_CREATE_TRIP: 4,
} as const;

const SOURCE_CLOSE_ONLY_MIN = 1;
const SOURCE_CLOSE_ONLY_MAX = 2;

interface ErrorModalProps {
  modal_state: boolean;
  close_modal: () => void;
  error: number;
  error_source: number;
  toggle_booking_pickup: () => void;
  toggle_booking_dropoff: () => void;
}

function ErrorModal({
  modal_state,
  close_modal,
  error,
  error_source,
  toggle_booking_pickup,
  toggle_booking_dropoff,
}: Readonly<ErrorModalProps>): React.ReactElement {
  const { locale } = useRouter();
  const fare_estimate_context = useFareEstimateContext();

  const isCloseOnlySource =
    error_source >= SOURCE_CLOSE_ONLY_MIN &&
    error_source <= SOURCE_CLOSE_ONLY_MAX;

  const invalidPickupHandler = (): void => {
    if (isCloseOnlySource) {
      close_modal();
    } else {
      toggle_booking_pickup();
    }
  };

  const invalidDropHandler = (): void => {
    if (isCloseOnlySource) {
      close_modal();
    } else {
      toggle_booking_dropoff();
    }
  };

  useFareEstimateRetryHook();

  const handleRetry = (): void => fare_estimate_context.set_is_retry(true);

  let errorContent: React.ReactElement;
  switch (error) {
    case FARE_ESTIMATE_ERROR.INVALID_TOKEN:
      errorContent = (
        <FareEstimateErrorCmp
          image={error_something_went_wrong}
          content={error_something_wrong}
          button_text={error_something_wrong_button}
          button_action={handleRetry}
        />
      );
      break;
    case FARE_ESTIMATE_ERROR.INVALID_PICKUP:
      errorContent = (
        <FareEstimateErrorCmp
          image={error_invalid_location}
          content={getErrorUnserviceablePickup(locale)}
          button_text={getPickupButtonLabel(locale)}
          button_action={invalidPickupHandler}
        />
      );
      break;
    case FARE_ESTIMATE_ERROR.INVALID_DROP:
      errorContent = (
        <FareEstimateErrorCmp
          image={error_invalid_location}
          content={getErrorUnserviceableDropoff(locale)}
          button_text={getDropOffButtonLabel(locale)}
          button_action={invalidDropHandler}
        />
      );
      break;
    default:
      errorContent = (
        <FareEstimateErrorCmp
          image={error_something_went_wrong}
          content={error_something_wrong}
          button_text={error_something_wrong_button}
          button_action={close_modal}
        />
      );
      break;
  }

  return (
    <ReactModal
      isOpen={modal_state}
      onRequestClose={close_modal}
      closeTimeoutMS={MODAL_CLOSE_TIMEOUT_NONE}
      overlayClassName={styles['overlay']}
      className={styles['modal-container']}
      ariaHideApp={false}
      shouldCloseOnOverlayClick
    >
      <div className={styles['error-modal']}>
        <button
          type="button"
          aria-label="Close"
          className={styles['close']}
          onClick={close_modal}
        >
          <NextImageV2
            src={crossIcon}
            height={16}
            width={16}
            quality={100}
            alt="Close"
          />
        </button>
        {errorContent}
      </div>
    </ReactModal>
  );
}

export default ErrorModal;
